import { useEffect, useRef, useState } from 'react';
import { AudioAnalyserService } from '../services/audio-analyser.service';

/**
 * Connects a mic stream to an AudioAnalyserService while dictating, so the
 * waveform pill animates with real audio levels. Separate from voice mode's
 * analyser - the Web Speech API manages its own mic internally, so we open
 * a parallel stream just for amplitude analysis (no audio output, no echo).
 *
 * Returns null until getUserMedia resolves; the waveform falls back to idle
 * animation during that brief window.
 */
export function useDictateAnalyser(
  isListening: boolean
): AudioAnalyserService | null {
  const [analyser, setAnalyser] = useState<AudioAnalyserService | null>(null);
  const serviceRef = useRef<AudioAnalyserService | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  if (!serviceRef.current) {
    serviceRef.current = new AudioAnalyserService();
  }

  useEffect(() => {
    const service = serviceRef.current!;

    if (!isListening) {
      for (const track of streamRef.current?.getTracks() ?? []) track.stop();
      streamRef.current = null;
      service.disconnect();
      setAnalyser(null);
      return;
    }

    let cancelled = false;

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        if (cancelled) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }
        streamRef.current = stream;
        return service.connectMicStream(stream);
      })
      .then(() => {
        if (!cancelled) setAnalyser(service);
      })
      .catch(() => {
        // Permission denied or unavailable - idle animation plays instead
      });

    return () => {
      cancelled = true;
      for (const track of streamRef.current?.getTracks() ?? []) track.stop();
      streamRef.current = null;
      service.disconnect();
      setAnalyser(null);
    };
  }, [isListening]);

  useEffect(() => {
    const service = serviceRef.current!;
    return () => service.dispose();
  }, []);

  return analyser;
}
